import React, { useState, useEffect } from "react";
import StepNavigation from "../../components/addListing/StepNavigation";
import { useTranslation } from "react-i18next";
import "../../styles/steps.css";

export default function StepRenovationPhotosDescription({
  listing,
  setListing,
  onBack,
  onSubmit,
  submitting,
}) {
  const { t } = useTranslation();

  /* =========================
     REMOVE PHOTO
  ========================== */

  const handleRemovePhoto = (removeIndex) => {
    setListing((prev) => {
      const photos = Array.isArray(prev.photos)
        ? prev.photos
        : [];

      const removed = photos[removeIndex];

      if (removed?.preview) {
        URL.revokeObjectURL(removed.preview);
      }

      const updated = photos.filter(
        (_, i) => i !== removeIndex
      );

      return {
        ...prev,
        photos: updated,
      };
    });
  };


  /* =========================
     ADD PHOTOS
  ========================== */

  const handleFiles = (e) => {
    const selectedFiles = Array.from(e.target.files);

    if (!selectedFiles.length) return;

    const filesWithPreview = selectedFiles.map(
      (file) => ({
        file,
        preview: URL.createObjectURL(file),
      })
    );

    setListing((prev) => {
      const existingPhotos = Array.isArray(
        prev.photos
      )
        ? prev.photos
        : [];

      const combinedPhotos = [
        ...existingPhotos,
        ...filesWithPreview,
      ].slice(0, 10);

      if (
        existingPhotos.length +
          selectedFiles.length >
        10
      ) {
        alert(
          t("stepPhotos.maxPhotosError")
        );
      }

      return {
        ...prev,
        photos: combinedPhotos,
      };
    });

    e.target.value = "";
  };


  /* =========================
     PHOTO COUNT
  ========================== */

  const photosCount =
    listing.photos?.length || 0;


  /* =========================
     SUBMIT VALIDATION
  ========================== */

  const isSubmitDisabled =
    submitting ||
    !listing.photos?.length ||
    !listing.description?.trim() ||
    !listing.phone?.trim();


  /* =========================
     CLEANUP PREVIEWS
  ========================== */

  useEffect(() => {
    return () => {
      listing.photos?.forEach((p) => {
        if (p.preview) {
          URL.revokeObjectURL(p.preview);
        }
      });
    };
  }, []);


  return (
    <div className="step step-photos">

      <h2 className="step-title">
        {t("renovation.stepPhotosTitle")}
      </h2>


      {/* =========================
          PHOTOS
      ========================== */}

      <div className="photos-section">

        <div className="upload-label">
          <strong>
            {t("stepPhotos.uploadLabel")}
            <span className="required">*</span>
          </strong>
        </div>


        <label className="custom-file-upload">

          {t("stepPhotos.chooseFiles")}

          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFiles}
            disabled={photosCount >= 10}
          />

        </label>


        <span className="file-name">

          {photosCount > 0
            ? t(
                "stepPhotos.filesSelected",
                { count: photosCount }
              )
            : t(
                "stepPhotos.noFileChosen"
              )}

        </span>


        {photosCount > 0 && (
          <>
            <p>
              {photosCount} photo(s) selected
            </p>

            <div className="photos-preview">

              {listing.photos.map(
                (photoObj, index) => (

                  <div
                    key={index}
                    className="photo-thumb"
                  >

                    <img
                      src={
                        photoObj.preview ||
                        photoObj.url
                      }
                      alt={t(
                        "stepPhotos.photoPreviewAlt"
                      )}
                      className="photo-img"
                    />


                    <button
                      type="button"
                      className="photo-remove"
                      onClick={() =>
                        handleRemovePhoto(index)
                      }
                      aria-label={t(
                        "stepPhotos.removePhoto"
                      )}
                    >
                      ×
                    </button>

                  </div>

                )
              )}

            </div>
          </>
        )}

      </div>


      {/* =========================
          DESCRIPTION
      ========================== */}

      <div className="description-section">

        <label>

          <strong>
            {t(
              "stepPhotos.descriptionLabel"
            )}

            <span className="required">
              *
            </span>
          </strong>

        </label>


        <textarea
          rows={5}
          className="description-textarea"
          placeholder={t(
            "stepPhotos.descriptionPlaceholder"
          )}
          value={
            listing.description || ""
          }

          onChange={(e) => {

            const cleaned =
              e.target.value

                .replace(
                  /\u00A0/g,
                  " "
                )

                .replace(
                  /[\u2000-\u200F]/g,
                  " "
                )

                .replace(
                  /[\u2028\u2029\uFEFF]/g,
                  ""
                )

                .replace(
                  /[\u0000-\u001F]/g,
                  ""
                )

                .replace(
                  /\r/g,
                  "\n"
                )

                .replace(
                  /[\p{Extended_Pictographic}]/gu,
                  ""
                )

                .replace(
                  /[<>]/g,
                  ""
                );


            setListing((prev) => ({
              ...prev,
              description: cleaned,
            }));

          }}

          style={{
            width: "100%",
          }}
        />

      </div>


      {/* =========================
          PHONE
      ========================== */}

      <label className="form-label">

        {t("stepPhotos.phoneLabel")}

        <span className="required">
          *
        </span>

      </label>


      <input
        type="tel"
        className="phone-input"
        value={listing.phone || ""}

        onChange={(e) =>
          setListing((prev) => ({
            ...prev,
            phone: e.target.value,
          }))
        }

        placeholder={t(
          "stepPhotos.phonePlaceholder"
        )}
      />


      {/* =========================
          NAVIGATION
      ========================== */}

      <StepNavigation
        onBack={onBack}

        nextLabel={
          submitting
            ? t("common.uploading")
            : t("common.submit")
        }

        onNext={onSubmit}

        nextDisabled={
          isSubmitDisabled
        }
      />

    </div>
  );
}
